/**
 * MicHelp — botón "¿Problemas con el micrófono?" + panel de diagnóstico.
 *
 * Compromiso de la reunión con Santiago (18/08/2026): la causa raíz principal
 * de "GALIA no funciona" es el micrófono mal seleccionado — en particular
 * macOS enlazando el iPhone como micrófono vía Continuity (el estudiante le
 * habla al computador pero el mic activo es el teléfono), más ambientes
 * ruidosos y permisos del navegador.
 *
 * El panel muestra:
 *  1. El micrófono ACTIVO (nombre real del dispositivo) — destapa el caso
 *     iPhone/Mac de inmediato.
 *  2. Un medidor de nivel en vivo — "habla y mira si la barra se mueve".
 *  3. La lista de micrófonos disponibles y tips para cambiar el activo.
 */

import { useEffect, useRef, useState } from 'react';
import './InfoButton.css'; // reusa .info-button para el estilo base del botón
import './MicHelp.css';

/* ── Botón ── */

interface MicHelpButtonProps {
  onClick: () => void;
}

export function MicHelpButton({ onClick }: MicHelpButtonProps) {
  return (
    <button
      type="button"
      className="info-button mic-help-button"
      onClick={onClick}
      aria-label="¿Problemas con el micrófono?"
      title="¿Problemas con el micrófono?"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="22" />
      </svg>
      <span className="mic-help-badge" aria-hidden="true">?</span>
    </button>
  );
}

/* ── Panel ── */

interface MicHelpPanelProps {
  onClose: () => void;
}

export function MicHelpPanel({ onClose }: MicHelpPanelProps) {
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const [devices, setDevices] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const meterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let raf = 0;
    let cancelled = false;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) return;
        const track = stream.getAudioTracks()[0];
        setActiveLabel(track?.label || 'Micrófono predeterminado');

        // Lista de entradas disponibles (los labels solo aparecen con permiso).
        try {
          const all = await navigator.mediaDevices.enumerateDevices();
          if (!cancelled) {
            setDevices(
              all
                .filter((d) => d.kind === 'audioinput' && d.label)
                .map((d) => d.label)
            );
          }
        } catch { /* lista opcional */ }

        // Medidor de nivel: RMS del analizador → ancho de la barra (vía ref,
        // sin re-render por frame).
        ctx = new AudioContext();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);
          const pct = Math.min(100, Math.round(rms * 300));
          if (meterRef.current) meterRef.current.style.width = `${pct}%`;
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        if (!cancelled) {
          setError(
            'No pudimos acceder al micrófono. Revisa que el navegador tenga permiso (ícono de candado junto a la dirección) y que ningún otro programa lo esté usando.'
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close().catch(() => undefined);
    };
  }, []);

  return (
    <div className="mic-help-overlay" onClick={onClose} role="dialog" aria-label="Ayuda del micrófono">
      <div className="mic-help-panel" onClick={(e) => e.stopPropagation()}>
        <div className="mic-help-header">
          <h2 className="mic-help-title">¿Problemas con el micrófono? 🎙️</h2>
          <button type="button" className="mic-help-close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        {error ? (
          <p className="mic-help-error">{error}</p>
        ) : (
          <>
            <div className="mic-help-active">
              <span className="mic-help-label">Micrófono activo:</span>
              <strong>{activeLabel ?? 'Detectando…'}</strong>
              {activeLabel && /iphone|ipad/i.test(activeLabel) && (
                <p className="mic-help-warning">
                  ⚠️ ¡Tu micrófono activo es un iPhone/iPad, no el del computador!
                  Desactívalo en el menú de micrófono del navegador o aleja el
                  teléfono, y recarga la página.
                </p>
              )}
            </div>

            <div className="mic-help-meter-wrap">
              <span className="mic-help-label">Habla y mira si la barra se mueve:</span>
              <div className="mic-help-meter">
                <div className="mic-help-meter-bar" ref={meterRef} />
              </div>
            </div>

            {devices.length > 1 && (
              <div className="mic-help-devices">
                <span className="mic-help-label">Micrófonos disponibles:</span>
                <ul>
                  {devices.map((d) => (
                    <li key={d} className={d === activeLabel ? 'active' : ''}>
                      {d}{d === activeLabel ? ' ← en uso' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <div className="mic-help-tips">
          <span className="mic-help-label">Tips:</span>
          <ol>
            <li>Si la barra no se mueve al hablar, el navegador está escuchando otro micrófono: cámbialo en el ícono de micrófono/candado de la barra de direcciones y recarga.</li>
            <li>Galia se confunde con música de fondo o varias voces cerca — búscate un lugar tranquilo o usa audífonos con micrófono.</li>
            <li>Si usas Galia dentro de Apolearn y el micrófono no responde, usa el botón «Abrir en pantalla completa».</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default MicHelpPanel;
